'use client'

import { useEffect, useState } from 'react'
import { AlertTriangle, Leaf, Wine, ArrowRight } from 'lucide-react'
import Link from '@/modules/shared/components/Link'
import { getHsCodeListDetail } from '@/modules/hs/api/homepage.api'
import { TrendingHsCode } from '@/modules/hs/types/homepage.types'
import { formatNumber } from '@/modules/shared/utils/number.util'

// Hardcoded anti-dumping data
const antiDumpingData = [
  { code: '72091690', origin: 'China' },
  { code: '72091891', origin: 'China' },
  { code: '72112320', origin: 'China' },
]

const ENVIRONMENTAL_HS_CODES = ['27101231', '38089410', '27021000']
const SPECIAL_CONSUMPTION_HS_CODES = ['22060041', '22089040', '24029010']

export default function SpecialTaxSection() {
  const [environmentalData, setEnvironmentalData] = useState<TrendingHsCode[]>([])
  const [specialConsumptionData, setSpecialConsumptionData] = useState<TrendingHsCode[]>([])
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    async function fetchData() {
      try {
        const [environmental, specialConsumption] = await Promise.all([
          getHsCodeListDetail(ENVIRONMENTAL_HS_CODES),
          getHsCodeListDetail(SPECIAL_CONSUMPTION_HS_CODES),
        ])

        setEnvironmentalData(environmental)
        setSpecialConsumptionData(specialConsumption)
      } catch (error) {
        console.error('Failed to fetch special tax data:', error)
      } finally {
        setIsLoading(false)
      }
    }

    fetchData()
  }, [])
  return (
    <section className="tax-tables-section rounded-xl ">
      <div className=" px-8">
        <div
          className="section-header"
          style={{ marginBottom: 'var(--spacing-8)' }}
        >
          <span className="section-badge section-bold-blue">CÁC LOẠI THUẾ NHẬP KHẨU BỔ SUNG</span>
          <h2 className="section-title">
            Các loại thuế nhập khẩu bổ sung cần lưu ý
          </h2>
          <p className="section-subtitle">
            Kiểm tra mã HS của hàng hóa dự định nhập khẩu vào Việt Nam có bị áp dụng các loại thuế dưới đây không
          </p>
          <div style={{ color: 'var(--neutral-500)' }}>
            <span>Xem đầy đủ chi tiết diễn biến theo suất theo thời gian</span>
          </div>
        </div>

        <div className="tax-tables-grid">
          {/* Anti-dumping Tax Table */}
          <div className="tax-table-card cl-card-border shadow-sm">
            <div className="tax-table-header">
              <div className="tax-table-title">
                <AlertTriangle size={18} />
                Thuế Chống bán phá giá
              </div>
            </div>
            <table className="tax-data-table">
              <thead>
                <tr>
                  <th>MÃ HS</th>
                  <th>XUẤT XỨ</th>
                </tr>
              </thead>
              <tbody>
                {antiDumpingData.map((item) => (
                  <tr key={item.code}>
                    <td>
                      <Link href={`/chi-tiet-ma-hs/${item.code}`} className="cl-hs-code" target='_blank'>
                        {item.code}
                      </Link>
                    </td>
                    <td>{item.origin}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="tax-table-card-footer">
              <Link href="/ket-qua-tra-cuu-ma-hs?taxTypes=CBPG" className="tax-table-link" target='_blank'>
                Xem thêm
                <ArrowRight size={14} />
              </Link>
            </div>
          </div>

          {/* Environmental Protection Tax Table */}
          <div className="tax-table-card cl-card-border shadow-sm">
            <div className="tax-table-header">
              <div className="tax-table-title">
                <Leaf size={18} />
                Thuế Bảo vệ môi trường
              </div>
            </div>
            {isLoading ? (
              <div className="flex justify-center items-center py-8">
                <div className="text-sm text-[#737373]">Đang tải...</div>
              </div>
            ) : (
              <table className="tax-data-table">
                <thead>
                  <tr>
                    <th>MÃ HS</th>
                    <th>BIÊN ĐỘ</th>
                  </tr>
                </thead>
                <tbody>
                  {environmentalData.map((item) => (
                    <tr key={item.hsCode}>
                      <td>
                        <Link href={`/chi-tiet-ma-hs/${item.hsCode}`} className="cl-hs-code" target='_blank'>
                          {item.hsCode}
                        </Link>
                      </td>
                      <td>
                        <span className="tax-rate">
                          {item.taxes.bvmt !== null ? `${formatNumber(item.taxes.bvmt)} VND/${item.unit}` : item.unit}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <div className="tax-table-card-footer">
              <Link
                href="/ket-qua-tra-cuu-ma-hs?taxTypes=BVMT"
                className="tax-table-link"
                target='_blank'
              >
                Xem thêm
                <ArrowRight size={14} />
              </Link>
            </div>
          </div>

          {/* Special Consumption Tax Table */}
          <div className="tax-table-card cl-card-border shadow-sm">
            <div className="tax-table-header">
              <div className="tax-table-title">
                <Wine size={18} />
                Thuế Tiêu thụ đặc biệt
              </div>
            </div>
            {isLoading ? (
              <div className="flex justify-center items-center py-8">
                <div className="text-sm text-[#737373]">Đang tải...</div>
              </div>
            ) : (
              <table className="tax-data-table">
                <thead>
                  <tr>
                    <th>MÃ HS</th>
                    <th>THUẾ SUẤT</th>
                  </tr>
                </thead>
                <tbody>
                  {specialConsumptionData.map((item) => (
                    <tr key={item.hsCode}>
                      <td>
                        <Link href={`/chi-tiet-ma-hs/${item.hsCode}`} className="cl-hs-code">
                          {item.hsCode}
                        </Link>
                      </td>
                      <td>
                        <span className="tax-rate">
                          {item.taxes.ttdb !== null ? `${item.taxes.ttdb}%` : 'N/A'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <div className="tax-table-card-footer">
              <Link
                href="/ket-qua-tra-cuu-ma-hs?taxTypes=TTDB"
                className="tax-table-link"
              >
                Xem thêm
                <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
